import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Button, Col, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Form, Row } from "reactstrap";
import ButtonComponent from "../../../../components/ButtonComponent";
import ControlledRowTextField from "../../../../components/ControlledRowTextField";
import ControlledRowTextFieldAddress from "../../../../components/ControlledRowTextFieldAddress";
import Map from "../../../../components/LocationSelectorModal";
import {
  Organization,
  useGetOrganizationByIdQuery,
  useUpdateOrganizationMutation,
} from "../../../../redux/services/orgService";

interface FormData {
  displayName: string;
  code: string;
  status: string | { value: string; label: string };
  supportsDeliveryPoint: boolean;
  deliveryAddress?: string;
  deliveryLatitude?: number;
  deliveryLongitude?: number;
  deliveryContactName?: string;
  deliveryContactPhone?: string;
  allowCreditOrders: boolean;
  creditLimit?: number;
  creditTermsDays?: number;
  memberCreditLimit?: number;
}

// const STATUS_OPTIONS = [
//   { value: "Approved", label: "Approved" },
//   { value: "Deleted", label: "Deleted" },
// ];

interface ViewOrgProps {
  organization?: any;
  organizationId?: string;
}

const ViewOrg: React.FC<ViewOrgProps> = ({ organization, organizationId }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Use the organizationId from props or from URL params
  const orgId = organizationId || id;

  const {
    data: organizationData,
    isLoading,
    error,
    refetch,
  } = useGetOrganizationByIdQuery(orgId!);

  // Use the mutation hook for update
  const [updateOrganization, { isLoading: isUpdating }] =
    useUpdateOrganizationMutation();

  const [showModal, setShowModal] = useState(false);
  const [hasSetFormData, setHasSetFormData] = useState(false);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
    register,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      supportsDeliveryPoint: false,
      allowCreditOrders: false,
    },
  });

  // Watch for conditional field changes
  const supportsDeliveryPoint = watch("supportsDeliveryPoint");
  const allowCreditOrders = watch("allowCreditOrders");

  // Reset form when organization data is loaded - more robust approach
  useEffect(() => {
    if (organizationData && !hasSetFormData) {
      const formData = {
        displayName: organizationData.displayName || "",
        code: organizationData.code || "",
        status: organizationData.status
          ? { value: organizationData.status, label: organizationData.status }
          : { value: "Approved", label: "Approved" },
        supportsDeliveryPoint: organizationData.supportsDeliveryPoint || false,
        deliveryAddress: organizationData.deliveryPoint?.deliveryAddress || "",
        deliveryLatitude: organizationData.deliveryPoint?.deliveryLatitude || 0,
        deliveryLongitude:
          organizationData.deliveryPoint?.deliveryLongitude || 0,
        deliveryContactName:
          organizationData.deliveryPoint?.deliveryContactName || "",
        deliveryContactPhone:
          organizationData.deliveryPoint?.deliveryContactPhone || "",
        allowCreditOrders:
          organizationData.creditSettings?.allowCreditOrders || false,
        creditLimit: organizationData.walletData?.creditLimit || 0,
        creditTermsDays: organizationData.creditSettings?.creditTermsDays || 0,
        memberCreditLimit:
          organizationData.creditSettings?.memberDefaultLimit || 0,
        currentOutstanding:
          organizationData.walletData?.currentOutstanding || 0,
      };

      reset(formData);
      setHasSetFormData(true);
    }
  }, [organizationData, reset, hasSetFormData]);

  // Refetch data when component mounts or id changes
  useEffect(() => {
    if (orgId) {
      refetch();
      setHasSetFormData(false);
    }
  }, [orgId, refetch]);

  // Handle error state
  useEffect(() => {
    if (error) {
      console.error("Error loading organization:", error);
      toast.error("Error loading organization data");
      navigate("/organizations/organization-list");
    }
  }, [error, navigate]);

  const handleLocationSave = (
    latitude: number,
    longitude: number,
    address: string
  ) => {
    setValue("deliveryLatitude", latitude);
    setValue("deliveryLongitude", longitude);
    setValue("deliveryAddress", address);
    setShowModal(false);
  };

  const handleLocationModalClose = () => {
    setShowModal(false);
  };

  const onSubmit = async (data: FormData) => {
    if (!organizationData || !orgId) return;

    try {
      // Prepare payload for update according to the API requirements
      const payload: Organization = {
        displayName: data.displayName,
        code: data.code,
        status:"Approved",
        supportsDeliveryPoint: data.supportsDeliveryPoint,
        allowCreditOrders: data.allowCreditOrders,
        // Delivery point fields - send empty values if supportsDeliveryPoint is false
        ...(data.supportsDeliveryPoint
          ? {
              deliveryAddress: data.deliveryAddress,
              deliveryLatitude: data.deliveryLatitude
                ? parseFloat(data.deliveryLatitude.toString())
                : 0,
              deliveryLongitude: data.deliveryLongitude
                ? parseFloat(data.deliveryLongitude.toString())
                : 0,
              deliveryContactName: data.deliveryContactName,
              deliveryContactPhone: data.deliveryContactPhone,
            }
          : {
              deliveryPointName: "",
              deliveryAddress: "",
              deliveryLatitude: 0,
              deliveryLongitude: 0,
              deliveryContactName: "",
              deliveryContactPhone: "",
            }),
        // Credit fields - send empty values if allowCreditOrders is false
        ...(data.allowCreditOrders
          ? {
              creditLimit: data.creditLimit
                ? parseFloat(data.creditLimit.toString())
                : 0,
              creditTermsDays: data.creditTermsDays
                ? parseFloat(data.creditTermsDays.toString())
                : 0,
              memberCreditLimit: data.memberCreditLimit
                ? parseFloat(data.memberCreditLimit.toString())
                : 0,
            }
          : {
              creditLimit: 0,
              creditTermsDays: 0,
              memberCreditLimit: 0,
            }),
      };

      // Call the update mutation
      await updateOrganization({
        organizationId: orgId,
        data: payload,
      }).unwrap();

      toast.success("Organization Updated Successfully.");
      navigate("/organizations/organization-list");

      // Refetch the updated data
      await refetch();
    } catch (error: any) {
      console.error("Could not update Organization", error);
      toast.error(error?.data?.message || "Could not update Organization");
    }
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <span className="ms-2">Loading organization...</span>
      </div>
    );
  }

  if (!organizationData && !isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="alert alert-danger">Organization not found</div>
        <ButtonComponent
          titel="Back to List"
          onClick={() => navigate("/organizations/organization-list")}
        />
      </div>
    );
  }

  return (
    <div>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {/* Name Field */}
        <ControlledRowTextField
          label={"Name"}
          inputName={"displayName"}
          autoComplete={"off"}
          inputType={"text"}
          placeholder={"Enter Organization name"}
          control={control}
          rules={{ required: "Name is required." }}
          errors={errors}
        />
        <Row>
          <Col lg="4">{""}</Col>
          <Col lg="8">
            {errors.displayName && (
              <span className="validation-message text-danger">
                {errors.displayName.message}
              </span>
            )}
          </Col>
        </Row>
        <hr />

        {/* Code Field */}
        <ControlledRowTextField
          label={"Code"}
          inputName={"code"}
          autoComplete={"off"}
          inputType={"text"}
          placeholder={"Enter Organization code"}
          control={control}
          rules={{ required: "Code is required." }}
          errors={errors}
        />
        <Row>
          <Col lg="4">{""}</Col>
          <Col lg="8">
            {errors.code && (
              <span className="validation-message text-danger">
                {errors.code.message}
              </span>
            )}
          </Col>
        </Row>
        <hr />

        {/* Status Field */}
        {/* <ControlledSelect
          label="Status"
          inputName="status"
          control={control}
          optionsList={STATUS_OPTIONS}
          rules={{ required: "Please select status." }}
          errors={errors}
          placeholder="Select status"
          isClear={false}
        />
        <Row>
          <Col lg="4">{""}</Col>
          <Col lg="8">
            {errors.status && (
              <span className="validation-message text-danger ">
                {errors.status.message}
              </span>
            )}
          </Col>
        </Row> */}

        {/* Delivery Point Checkbox */}
        <Row className="mb-3">
          <Col lg="4">
            <label className="form-label">Delivery Point</label>
          </Col>
          <Col lg="8">
            <input
              type="checkbox"
              {...register("supportsDeliveryPoint")}
              className="form-check-input"
            />
          </Col>
        </Row>
        <hr />

        {/* Conditional Delivery Address Fields */}
        {supportsDeliveryPoint && (
          <>
            <ControlledRowTextFieldAddress
              label={"Delivery Address"}
              inputName={"deliveryAddress"}
              inputType={"text"}
              placeholder={"Select Delivery Address From Location Button"}
              control={control}
              setShowModal={setShowModal}
              rules={{ required: "Delivery address is required" }}
              errors={errors}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.deliveryAddress && (
                  <span className="validation-message text-danger">
                    {errors.deliveryAddress.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />

            {/* Hidden Latitude/Longitude Fields */}
            <Row className="d-none">
              <Col lg="6">
                <ControlledRowTextField
                  label={"Delivery Latitude"}
                  inputName={"deliveryLatitude"}
                  inputType={"number"}
                  placeholder={"Enter Latitude"}
                  control={control}
                  isDisabled={true}
                />
              </Col>
              <Col lg="6">
                <ControlledRowTextField
                  label={"Delivery Longitude"}
                  inputName={"deliveryLongitude"}
                  inputType={"number"}
                  placeholder={"Enter Longitude"}
                  control={control}
                  isDisabled={true}
                />
              </Col>
            </Row>

            {/* Delivery Contact Fields */}
            <ControlledRowTextField
              label={"Delivery Contact Name"}
              inputName={"deliveryContactName"}
              autoComplete={"off"}
              inputType={"text"}
              placeholder={"Enter delivery contact name"}
              control={control}
              rules={{ required: "Delivery contact name is required" }}
              errors={errors}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.deliveryContactName && (
                  <span className="validation-message text-danger">
                    {errors.deliveryContactName.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />

            <ControlledRowTextField
              label={"Delivery Contact Phone"}
              inputName={"deliveryContactPhone"}
              autoComplete={"off"}
              inputType={"text"}
              placeholder={"Enter delivery contact phone"}
              control={control}
              rules={{ required: "Delivery contact phone is required" }}
              errors={errors}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.deliveryContactPhone && (
                  <span className="validation-message text-danger">
                    {errors.deliveryContactPhone.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />
          </>
        )}

        {/* Allow Credit Orders Checkbox */}
        <Row className="mb-3">
          <Col lg="4">
            <label className="form-label">Allow Credit Orders</label>
          </Col>
          <Col lg="8">
            <input
              type="checkbox"
              {...register("allowCreditOrders")}
              className="form-check-input"
            />
          </Col>
        </Row>
        <hr />

        {/* Conditional Credit Fields */}
        {allowCreditOrders && (
          <>
            <ControlledRowTextField
              label={"Credit Limit"}
              inputName={"creditLimit"}
              autoComplete={"off"}
              inputType={"number"}
              placeholder={"Enter credit limit"}
              control={control}
              rules={{ required: "Credit limit is required" }}
              errors={errors}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.creditLimit && (
                  <span className="validation-message text-danger">
                    {errors.creditLimit.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />

            <ControlledRowTextField
              label={"Credit Terms (Days)"}
              inputName={"creditTermsDays"}
              autoComplete={"off"}
              inputType={"number"}
              placeholder={"Enter credit terms in days"}
              control={control}
              rules={{ required: "Credit terms is required" }}
              errors={errors}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.creditTermsDays && (
                  <span className="validation-message text-danger">
                    {errors.creditTermsDays.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />

            <ControlledRowTextField
              label={"Member Credit Limit"}
              inputName={"memberCreditLimit"}
              autoComplete={"off"}
              inputType={"number"}
              placeholder={"Enter member credit limit"}
              control={control}
              rules={{ required: "Member credit limit is required" }}
              errors={errors}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.memberCreditLimit && (
                  <span className="validation-message text-danger">
                    {errors.memberCreditLimit.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />
          </>
        )}
        <ControlledRowTextField
          label={"Current Outstanding"}
          inputName={"currentOutstanding"}
          autoComplete={"off"}
          inputType={"number"}
          placeholder={"Current outstanding amount"}
          control={control}
          isDisabled={true}
          rules={{}}
          errors={errors}
        />
        <Row>
          <Col lg="4">{""}</Col>
          <Col lg="8">
            <small className="text-muted">
              This field is automatically calculated and cannot be edited
            </small>
          </Col>
        </Row>
        <hr />

        {/* Submit Button */}
        <div className="d-flex justify-content-end gap-2">
          {isUpdating ? (
            <ButtonComponent
              titel="Updating..."
              isDisabled={true}
              icon={<FontAwesomeIcon icon={faSpinner} spin />}
            />
          ) : (
            <ButtonComponent titel="Update Organization" type="submit" />
          )}
        </div>
      </Form>

      {/* Location Selection Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Delivery Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Map onSaveLocation={handleLocationSave} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => handleLocationModalClose()}
          >
            Save Location
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ViewOrg;
